"use client";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Eye, Gift } from "lucide-react";

export default function GiftProductCard({ product, setLoading }: any) {
  const discountPercent = product.productAttributes?.[0]?.discountPercent || 0;
  const stock = product.productAttributes?.[0]?.stockAmount ?? 0;

  const [isMobile, setIsMobile] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ===== 3D TILT ===== */
  const handleMouseMove = (e: any) => {
    if (!cardRef.current || isMobile) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rx = -(y / rect.height - 0.5) * 7;
    const ry = (x / rect.width - 0.5) * 7;

    cardRef.current.style.transform = `
      perspective(900px)
      rotateX(${rx}deg)
      rotateY(${ry}deg)
      translateY(-4px)
    `;
  };

  const resetTilt = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform =
      "perspective(900px) rotateX(0deg) rotateY(0deg)";
  };

  const handleNavigateToDetails = () => {
    setLoading && setLoading(true);
    window.location.href = `/product-details/${product.slug}`;
  };

  return (
    <div
      ref={cardRef}
      className="product-card-theme"
      onMouseMove={handleMouseMove}
      onMouseLeave={resetTilt}
      onClick={handleNavigateToDetails}
    >
      {/* FLOATING SPARKLES */}
      <div className="sparkle s1">✨</div>
      <div className="sparkle s2">🎁</div>
      <div className="sparkle s3">✨</div>
      <div className="sparkle s4">🎀</div>

      {/* IMAGE */}
      <div className="product-image-wrapper">
        <Image
          src={product?.images?.[0]?.image ?? "/noimage.png"}
          alt={product.name}
          fill
          className="product-image"
        />

        {/* IMAGE SHINE */}
        <span className="image-shine" />

        {discountPercent > 0 && (
          <div className="discount-badge">
            🎉 {discountPercent}% OFF
          </div>
        )}

        {stock <= 0 && <div className="out-stock-badge">Out of Stock</div>}
      </div>

      {/* INFO */}
      <div className="info-wrapper">
        <h3 className="product-title">{product.name}</h3>

        <div className="price-row">
          <span className="sale-price">
            ৳ {product.productAttributes?.[0]?.discountedRetailPrice}
          </span>

          {discountPercent > 0 && (
            <span className="regular-price">
              ৳ {product.productAttributes?.[0]?.retailPrice}
            </span>
          )}
        </div>

        <button className="quick-view-btn">
          <Gift size={18} />
          <span>View Gift</span>
        </button>
      </div>

      {/* ================= CSS ================= */}
      <style jsx>{`
        /* ---------------- CARD ---------------- */
        .product-card-theme {
          width: 100%;
          height: 520px;
          background: linear-gradient(135deg, #FFFBF8 0%, #FFF9F5 100%);
          border-radius: 24px;
          border: 2px solid #f7f7f7ff;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          display: flex;
          flex-direction: column;
          transition: transform 0.15s ease, box-shadow 0.15s ease, border 0.3s ease;
          box-shadow: 
            0 2px 4px rgba(255, 255, 255, 0.08),
            0 4px 8px rgba(255,140,83,0.1),
            0 8px 16px rgba(255,140,83,0.12),
            0 16px 32px rgba(63, 63, 63, 0.08),
            0 1px 0 rgba(255,255,255,0.8) inset,
            0 -1px 0 rgba(255,200,150,0.2) inset;
        }

        .product-card-theme:hover {
          box-shadow: 
            0 2px 4px rgba(255, 255, 255, 0.08),
            0 4px 8px rgba(255,140,83,0.1),
            0 8px 16px rgba(255,140,83,0.12),
            0 16px 32px rgba(63, 63, 63, 0.08),
            0 1px 0 rgba(255,255,255,0.8) inset,
            0 -1px 0 rgba(255,200,150,0.2) inset;
          border-color: #f7f7f7ff;
        }

        /* ---------------- SHINE SWEEP ON CARD ---------------- */
        .product-card-theme::before {
          content: "";
          position: absolute;
          top: 0;
          left: -120%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            130deg,
            transparent,
            rgba(255,182,193,0.35),
            transparent
          );
          transform: skewX(-20deg);
          pointer-events: none;
          z-index: 2;
        }

        .product-card-theme:hover::before {
          animation: shineCard 0.9s ease forwards;
        }

        @keyframes shineCard {
          to { left: 140%; }
        }

        /* ---------------- FLOATING SPARKLES ---------------- */
        .sparkle {
          position: absolute;
          font-size: 16px;
          opacity: 0;
          pointer-events: none;
          z-index: 4;
        }

        .product-card-theme:hover .sparkle {
          animation: float 2s ease-in-out infinite;
        }

        .s1 {
          top: 15%;
          left: 10%;
          animation-delay: 0s !important;
        }

        .s2 {
          top: 25%;
          right: 12%;
          animation-delay: 0.3s !important;
        }

        .s3 {
          top: 50%;
          left: 8%;
          animation-delay: 0.6s !important;
        }

        .s4 {
          top: 60%;
          right: 10%;
          animation-delay: 0.9s !important;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.8;
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
            opacity: 1;
          }
          90% {
            opacity: 0.8;
          }
        }

        /* ---------------- IMAGE ---------------- */
        .product-image-wrapper {
          width: 100%;
          aspect-ratio: 1 / 1.1;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #FFF5F0 0%, #FFE9E0 100%);
          border-radius: 20px 20px 0 0;
        }

        .product-image {
          object-fit: cover;
          transition: transform 0.35s ease, filter 0.3s ease;
        }

        .product-card-theme:hover .product-image {
          transform: scale(1.08);
          filter: brightness(1.05);
        }

        /* ---------------- IMAGE SHINE ---------------- */
        .image-shine {
          position: absolute;
          inset: 0;
          left: -120%;
          width: 45%;
          height: 100%;
          background: linear-gradient(
            120deg,
            transparent,
            rgba(255,215,180,0.6),
            transparent
          );
          transform: skewX(-18deg);
          pointer-events: none;
          z-index: 2;
        }

        .product-card-theme:hover .image-shine {
          animation: shineImage 0.7s ease forwards;
        }

        @keyframes shineImage {
          to {
            left: 150%;
          }
        }

        /* BADGES */
        .discount-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
          color: white;
          padding: 8px 14px;
          border-radius: 50px;
          font-size: 12px;
          font-weight: 800;
          z-index: 3;
          box-shadow: 
            0 2px 4px rgba(255,107,107,0.3),
            0 4px 8px rgba(255,107,107,0.25),
            0 8px 16px rgba(255,107,107,0.2),
            0 16px 32px rgba(255,107,107,0.15);
          letter-spacing: 0.5px;
        }

        .out-stock-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(0,0,0,0.85);
          backdrop-filter: blur(10px);
          color: white;
          padding: 8px 14px;
          border-radius: 50px;
          font-size: 12px;
          font-weight: 700;
          z-index: 3;
        }

        /* ---------------- INFO ---------------- */
        .info-wrapper {
          padding: 18px 16px 16px;
          display: flex;
          flex-direction: column;
          flex: 1;
          background: linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,249,245,0.98) 100%);
        }

        .product-title {
          font-size: ${isMobile ? "14px" : "17px"};
          font-weight: 700;
          color: #2C2C2C;
          min-height: ${isMobile ? "36px" : "44px"};
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .price-row {
          display: flex;
          gap: 10px;
          align-items: center;
          margin-bottom: 12px;
        }

        .sale-price {
          font-size: ${isMobile ? "20px" : "24px"};
          font-weight: 900;
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .regular-price {
          font-size: ${isMobile ? "13px" : "15px"};
          color: #999;
          text-decoration: line-through;
          font-weight: 600;
        }

     
        .quick-view-btn {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
          border: 2px solid rgba(255,255,255,0.3);
          color: #fff;
          padding: 12px 0;
          border-radius: 50px;
          display: flex;
          gap: 8px;
          justify-content: center;
          align-items: center;
          font-weight: 800;
          letter-spacing: 0.5px;
          font-size: ${isMobile ? "14px" : "15px"};
          box-shadow: 
            0 2px 4px rgba(255,107,107,0.25),
            0 4px 8px rgba(255,107,107,0.2),
            0 8px 16px rgba(255,107,107,0.15),
            0 16px 32px rgba(255,107,107,0.1),
            0 1px 0 rgba(255,255,255,0.3) inset,
            0 -1px 0 rgba(0,0,0,0.1) inset;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .quick-view-btn::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255,255,255,0.3);
          transform: translate(-50%, -50%);
          transition: width 0.5s ease, height 0.5s ease;
        }

        .quick-view-btn:hover::before {
          width: 300px;
          height: 300px;
        }

        .quick-view-btn:hover {
          transform: translateY(-3px);
          box-shadow: 
            0 4px 8px rgba(255,107,107,0.3),
            0 8px 16px rgba(255,107,107,0.25),
            0 16px 32px rgba(255,107,107,0.2),
            0 24px 48px rgba(255,107,107,0.18),
            0 32px 64px rgba(255,107,107,0.12),
            0 1px 0 rgba(255,255,255,0.4) inset,
            0 -2px 0 rgba(0,0,0,0.15) inset;
        }

        .quick-view-btn span,
        .quick-view-btn svg {
          position: relative;
          z-index: 1;
        }

        @media (max-width: 767px) {
          .product-card-theme {
            height: 360px;
          }

          .product-image-wrapper {
            border-radius: 16px 16px 0 0;
          }
        }
      `}</style>
    </div>
  );
}